import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { LOCALES, type Locale } from './locales'

export type Lang = 'en' | 'zh'

interface I18nState {
  lang: Lang
  setLang: (l: Lang) => void
  t: Locale
}

const I18nContext = createContext<I18nState | null>(null)

const STORAGE_KEY = 'orion-lang'

/**
 * RTL infrastructure is preserved for future right-to-left languages
 * (Arabic, Hebrew, etc.). Add the language code to this set when
 * re-enabling — `dir="rtl"` flips automatically.
 */
const RTL_LANGS: ReadonlySet<Lang> = new Set()

function loadInitial(): Lang {
  if (typeof window === 'undefined') return 'en'
  const saved = window.localStorage.getItem(STORAGE_KEY) as Lang | null
  if (saved && saved in LOCALES) return saved
  return 'en'
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(loadInitial)

  const setLang = useMemo(
    () => (l: Lang) => {
      setLangState(l)
      try {
        window.localStorage.setItem(STORAGE_KEY, l)
      } catch {
        // ignore
      }
    },
    [],
  )

  useEffect(() => {
    document.documentElement.lang = lang
    document.documentElement.dir = RTL_LANGS.has(lang) ? 'rtl' : 'ltr'
  }, [lang])

  const value = useMemo<I18nState>(
    () => ({ lang, setLang, t: LOCALES[lang] }),
    [lang, setLang],
  )

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
}

export function useI18n(): I18nState {
  const ctx = useContext(I18nContext)
  if (!ctx) throw new Error('useI18n must be used inside <I18nProvider>')
  return ctx
}

export function useT(): Locale {
  return useI18n().t
}

export const LANG_OPTIONS: { code: Lang; label: string; native: string }[] = [
  { code: 'en', label: 'EN', native: 'English' },
  { code: 'zh', label: 'ZH', native: '中文' },
]
